<template>
  <div id="admin-users">
    <v-card class="px-5 py-2 mt-4 mb-5 elevation-0">
      <v-row class="align-end">
        <v-col cols="12" md="4" class="my-0 py-0">
          <v-text-field
            v-model="filters.email"
            label="email"
            :disabled="loading"
            hide-details
            clearable
            @keydown.enter="updateFilters"
          />
        </v-col>
        <v-col cols="12" md="4" class="my-0 py-0">
          <v-text-field
            v-model="filters._id"
            label="userId"
            :disabled="loading"
            hide-details
            clearable
            @keydown.enter="updateFilters"
          />
        </v-col>
        <v-col cols="12" md="4" class="my-0 py-0">
          <v-text-field
            v-model="filters.uid"
            label="uid"
            :disabled="loading"
            hide-details
            clearable
            @keydown.enter="updateFilters"
          />
        </v-col>

        <v-col cols="12" md="4" class="my-0 py-0">
          <v-text-field
            v-model="filters.code"
            label="code"
            :disabled="loading"
            hide-details
            clearable
            @keydown.enter="updateFilters"
          />
        </v-col>
        <v-col cols="12" md="4" class="my-0 py-0">
          <v-text-field
            v-model="filters.referralCode"
            label="referral code"
            :disabled="loading"
            hide-details
            clearable
            @keydown.enter="updateFilters"
          />
        </v-col>

        <v-col cols="12" md="1" class="my-0 py-0">
          <v-btn color="primary" small class="mt-4" :loading="loading" @click="updateFilters">
            Buscar
          </v-btn>
        </v-col>
      </v-row>
    </v-card>
    <v-card class="px-5 py-2 mt-3 mb-5 elevation-0">
      <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        sort-by="createdAt"
        :sort-desc="true"
        class="admin-users-table elevation-0 mt-2"
        mobile-breakpoint="0"
        :loading="loading"
        :footer-props="{
          'items-per-page-options': defaultPaginationItemsPerPageOptions,
        }"
        :items-per-page="20"
        dense
        @update:options="loadItems"
      >
        <template v-slot:item.supplant="{ item }">
          <div class="mr-2">
            <v-btn
              small
              :color="$vuetify.theme.dark ? 'dark_Textgrey' : 'softInfo'"
              @click="supplantUser(item)"
            >
              <v-icon color="black">mdi-account-convert</v-icon>
            </v-btn>
          </div>
        </template>

        <template v-slot:item.email="{ item }">
          <b>
            <a @click="showUserDetail(item)">{{ item.email }}</a>
          </b>
        </template>

        <template v-slot:item.createdAt="{ item }">
          {{ getDateUserFullFormat(item.createdAt) }}
        </template>

        <template v-slot:item.status="{ item }">
          {{ $t(`views.myAccount.billing.status.${item.status}`) }}
        </template>

        <template v-slot:item.lastSignIn="{ item }">
          {{
            isInLast24h(item.lastSignIn)
              ? $t(`views.myAccount.partner.lastSignInLast24h`)
              : getDateUserFormat(item.lastSignIn)
          }}
        </template>

        <template v-slot:item.referralCode="{ item }">
          <div class="d-flex">
            <div class="mr-1">
              <b>{{ item.referralCode }}</b>
            </div>
            <div>
              <AdminUsersReferralCodeInput :user="item" @loadItems="loadItems" />
            </div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-navigation-drawer v-model="drawer" fixed right temporary width="800px" touchless>
      <div class="pa-5">
        <div class="items-flex justify-between">
          <div class="admin-user-drawer-email ml-3">
            {{ userDetail.email }}
          </div>

          <a @click="drawer = false">
            <v-icon>mdi-close</v-icon>
          </a>
        </div>
        <v-simple-table dense fixed-header class="mt-6">
          <template v-slot:default>
            <tbody>
              <tr v-for="key of Object.keys(userDetail)">
                <td>
                  <span v-if="key === 'totalInvestmentNoCash'" class="important-key">
                    totalInvestmentNoCash
                  </span>
                  <span v-else-if="key === 'numOrdersNoFxPlusDividends'" class="important-key">
                    {{ key }}
                  </span>
                  <span v-else>
                    {{ key }}
                  </span>
                </td>
                <td class="text-right input-small items-flex">
                  <span v-if="key === 'totalInvestmentNoCash'" class="important-key">
                    <currency
                      v-if="userDetail.totalInvestmentNoCash"
                      :value="userDetail.totalInvestmentNoCash"
                      :currency="userDetail.baseCurrency"
                    />
                    <span v-else>-</span>
                  </span>
                  <span v-else-if="key === 'numOrdersNoFxPlusDividends'" class="important-key">
                    {{ userDetail[key] }}
                  </span>
                  <span v-else-if="key === 'brokers'">
                    <v-chip-group column class="ma-0 pa-0 justify-end admin-users-drawer-brokers">
                      <v-chip
                        v-for="broker in userDetail[key]"
                        :key="broker._id"
                        small
                        :outlined="broker.custom"
                        class="mx-0"
                      >
                        {{ broker.custom ? broker.name : broker.code }}
                      </v-chip>
                    </v-chip-group>
                  </span>
                  <span
                    v-else-if="key === 'preferencesOptInTutorial'"
                    class="admin-users-drawer-preferencesOptInTutorial"
                  >
                    <v-switch
                      v-model="userDetail.preferencesOptInTutorial"
                      hide-details
                      dense
                      class="ma-0 pa-0"
                      color="softTextGrey"
                      @change="changePreferencesOptInTutorial(userDetail)"
                    ></v-switch>
                  </span>
                  <span v-else>
                    {{ userDetail[key] }}
                  </span>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <div class="mt-12 pt-10 text-right">
          <v-btn small outlined :loading="loading" color="softRed" @click="deleteUser(userDetail)">
            Eliminar usuario
          </v-btn>
        </div>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script>
import { adminGetUsers, adminGetUserSummary, adminUpdateUser, adminDeleteUser } from '@/lib/api.js'
import { mapMutations } from 'vuex'
import tableMixin from '@/views/components/core/tableMixin.js'
import AdminUsersReferralCodeInput from './AdminUsersReferralCodeInput.vue'
import { getDateUserFullFormat, getDateUserFormat, isInLast24h } from '@/lib/utils/date.js'
import Currency from '@/views/components/common/Currency.vue'

export default {
  components: { AdminUsersReferralCodeInput, Currency },
  mixins: [tableMixin],
  data: () => ({
    drawer: false,
    userDetail: {},
    filters: {},
  }),
  computed: {
    defaultHeaders() {
      return [
        {
          text: 'Supplant',
          value: 'supplant',
        },
        {
          text: 'email',
          value: 'email',
        },
        {
          text: 'createdAt',
          value: 'createdAt',
        },
        {
          text: 'status',
          value: 'status',
        },
        {
          text: 'trialDays',
          value: 'trialDays',
        },
        {
          text: 'referralCode',
          value: 'referralCode',
        },
        {
          text: 'lastSignIn',
          value: 'lastSignIn',
        },
      ]
    },
  },
  methods: {
    getDateUserFullFormat,
    getDateUserFormat,
    isInLast24h,
    ...mapMutations({
      setSupplantedUser: 'SET_SUPPLANTED_USER',
    }),
    updateFilters() {
      this.setFirstPage()
      this.loadItems()
    },
    async loadItems() {
      this.loading = true
      try {
        const filter = JSON.parse(JSON.stringify(this.filters))
        if (!filter.email || filter.email === '') delete filter.email
        if (!filter._id || filter._id === '') delete filter._id
        if (!filter.uid || filter.uid === '') delete filter.uid
        if (!filter.referralCode || filter.referralCode === '') delete filter.referralCode
        if (!filter.code || filter.code === '') delete filter.code

        const { page, limit, sort } = this.getTablePaginationOptions(this.options)
        const response = await adminGetUsers({ filter, page, limit, sort })
        this.setTablePaginatedResponse(response)
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loading = false
    },
    async showUserDetail(user) {
      this.loading = true
      const response = await adminGetUserSummary(user._id)
      this.userDetail = { _id: user._id, ...response.data }
      this.drawer = true
      this.loading = false
    },
    async supplantUser(user) {
      this.setSupplantedUser(user)
      this.$router.replace({
        name: 'portfolio',
      })
    },
    async changePreferencesOptInTutorial(userDetail) {
      this.loading = true

      try {
        const data = {
          preferences: {
            optInTutorial: userDetail.preferencesOptInTutorial,
          },
        }
        await adminUpdateUser(userDetail._id, data)

        await this.showUserDetail(userDetail)
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }

      this.loading = false
    },
    async deleteUser(user) {
      if (
        await this.$root.$confirm({
          title: 'Borrar usuario',
          message: 'Confirma que quieres borrar el usuario y TODO lo relacionado con el usuario',
        })
      ) {
        this.loading = true
        await adminDeleteUser(user._id)
        await this.loadItems()
        this.drawer = false
      }
    },
  },
}
</script>

<style lang="scss">
#admin-users {
  .admin-user-drawer-email {
    font-size: 20px;
    font-weight: 600;
  }
  .important-key {
    font-weight: 600;
  }
  .admin-users-drawer-brokers {
    .v-slide-group__content {
      justify-content: flex-end;
    }
  }
  .admin-users-drawer-preferencesOptInTutorial {
    .v-input__slot {
      justify-content: flex-end;
    }
  }
}
</style>
